import React, {
  ComponentType,
  useCallback,
  useId,
  useMemo,
  useState,
} from "react";

import type { Dayjs } from "dayjs";
import { ChevronDown, ChevronUp, Filter } from "react-feather";

import DatePickerJs from "../../../shared/DatePicker/DatePicker";
import ExportToXlsxButtonJs from "../../../CommonComponents/ExportToXlsxButton/ExportToXlsxButton";
import Button from "../../../shared/Button/Button";
import RebetLoaderJs from "../../../shared/RebetLoader/RebetLoader";
import GeographicUserInsightsJs from "../../../shared/Dashboard/GeographicUserInsights/GeographicUserInsights";
import Select from "../../../shared/Select/Select";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import { getAdminNow } from "../../../../utils/dateService";
import {
  defaultDateRangeForViewport,
  defaultStartRangeForViewport,
} from "../../../../utils/narrowViewportChartDates";
import { ChartCard } from "../../../shared/Charts";
import { useCurrencySymbol } from "../../../../hooks/useCurrencySymbol";
import {
  useGetSportsbookByState,
  SportsbookStateRow,
} from "../../../../queries/sportsbook-kpis/useGetSportsbookByState";

import styles from "./SportsbookByState.module.css";

const CustomDatePicker = DatePickerJs as ComponentType<any>;
const ExportToXlsxButton = ExportToXlsxButtonJs as ComponentType<any>;
const RebetLoader = RebetLoaderJs as ComponentType<any>;
const GeographicUserInsights = GeographicUserInsightsJs as ComponentType<any>;

type DateRange = [Dayjs, Dayjs];

type MetricKey = Exclude<keyof SportsbookStateRow, "name" | "abbr">;

const CURRENCY_METRICS = new Set<MetricKey>([
  "turnover",
  "pnl",
  "avg_bet_amount",
]);

interface SportsbookByStateProps {
  // Default to USA — when this view is wired to redux, derive from `appConfig`.
  country?: string;
}

const SportsbookByState: React.FC<SportsbookByStateProps> = ({
  country = "USA",
}) => {
  const [dates, setDates] = useState<DateRange>(() =>
    defaultDateRangeForViewport(() => {
      const now = getAdminNow();
      return [now.startOf("day"), now.endOf("day")];
    }),
  );
  const [startRange] = useState(() =>
    defaultStartRangeForViewport(AppStrings.TODAY),
  );
  const [metric, setMetric] = useState<MetricKey>("pnl");
  const [filtersVisible, setFiltersVisible] = useState(false);
  const filtersId = useId();

  const currencySymbol = useCurrencySymbol() ?? "$";

  // Date range drives the query key, so changing it triggers a refetch.
  const dateFilters = useMemo(
    () => ({
      start_date: dates[0].format("YYYY-MM-DD"),
      end_date: dates[1].format("YYYY-MM-DD"),
    }),
    [dates],
  );

  const { data: stateData = [], isFetching } =
    useGetSportsbookByState(dateFilters);

  // Options live in render so labels track strings.json reassignment.
  const metricOptions: { value: MetricKey; label: string }[] = [
    { value: "pnl", label: AppStrings.PROFIT_LOSS },
    { value: "turnover", label: AppStrings.TURNOVER },
    { value: "total_bets", label: AppStrings.TOTAL_BETS },
    { value: "avg_bet_amount", label: AppStrings.AVG_BET_AMOUNT },
    { value: "total_users", label: AppStrings.TOTAL_ACTIVE_USERS },
    { value: "margin_pct", label: AppStrings.MARGIN_PCT },
  ];

  const mapData = useMemo(
    () =>
      stateData.map((s) => ({
        name: s.name,
        abbr: s.abbr,
        amount: s[metric],
      })),
    [stateData, metric],
  );

  const getData = useCallback((_queryString: string | null) => {
    // Date changes flow through `dates` -> query key, which refetches.
  }, []);

  const exportRows = useMemo(
    () =>
      stateData.map((s) => ({
        state: s.name,
        abbreviation: s.abbr,
        turnover: s.turnover,
        profit_and_loss: s.pnl,
        margin_pct: s.margin_pct,
        total_bets: s.total_bets,
        avg_bet_amount: s.avg_bet_amount,
        total_users: s.total_users,
      })),
    [stateData],
  );

  return (
    <ChartCard
      title="Sportsbook by State"
      headerExtra={
        <>
          <span className={styles.filterToggle}>
            <Button
              onClick={() => setFiltersVisible((visible) => !visible)}
              aria-expanded={filtersVisible}
              aria-controls={filtersId}
              aria-label={AppStrings.PAM_REPORT_FILTERS}
            >
              <Filter size={18} aria-hidden="true" />
              {filtersVisible ? (
                <ChevronUp size={16} aria-hidden="true" />
              ) : (
                <ChevronDown size={16} aria-hidden="true" />
              )}
            </Button>
          </span>
          <div
            id={filtersId}
            className={`${styles.headerSelects}${
              filtersVisible ? "" : ` ${styles.filtersCollapsed}`
            }`}
            data-testid="sportsbook-by-state-filters"
          >
            <Select
              className={styles.metricSelect}
              value={metric}
              options={metricOptions}
              onChange={(value) => setMetric(value as MetricKey)}
              aria-label={AppStrings.METRIC}
            />
          </div>
        </>
      }
      bodyClassName={styles.mapContainer}
      controls={
        <>
          <CustomDatePicker
            getData={getData}
            dates={dates}
            setDates={setDates}
            showPresets={true}
            startRange={startRange}
          />
          <ExportToXlsxButton
            data={exportRows}
            filename="sportsbook-by-state.xlsx"
          />
        </>
      }
    >
      {isFetching ? (
        <div className={styles.loaderWrapper}>
          <RebetLoader width={100} height={100} />
        </div>
      ) : (
        <GeographicUserInsights
          mapId="sportsbook-by-state"
          data={mapData}
          isDollars={CURRENCY_METRICS.has(metric)}
          country={country}
          currencySymbol={currencySymbol}
          highlightNegativeValues
        />
      )}
    </ChartCard>
  );
};

export default SportsbookByState;
